import { Center, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'

import { NoWrap } from '~shared-components/components'
import { getInvoiceLabelByType } from '~shared-components/components/InvoiceCard/getInvoiceLabelByType'
import { RouterLink } from '~shared-components/components/RouterLink/RouterLink'
import {
  UseSearchData,
  UseSearchResultOptions,
} from '~shared-components/hooks/useSearch'
import { useSearchQuery } from '~shared-components/hooks/useSearchQuery'
import { useTranslations } from '~shared-components/hooks/useTranslations'
import { Product } from '~shared-components/model'
import {
  TransformedInvoice,
  TransformedListOrder,
  TransformedUser,
} from '~shared-components/transforms'
import { TFn } from '~shared-components/translations/translations'
import { Page } from '~shared-components/utils/enums'
import { trimLeadingZeros } from '~shared-components/utils/misc'

import { TextHighlight } from './Highlight'
import { ResultItem } from './ResultItem'
import { TabWithCount } from './TabWithCount'
import { ViewMoreLink } from './ViewMoreLink'

const getProductLabels = (query: string, product: Product, t: TFn) => [
  <React.Fragment key="product-number">
    <NoWrap>
      {t('label.productnumber')}
      :&nbsp;
    </NoWrap>
    <TextHighlight search={query.split(' ')}>
      {trimLeadingZeros(product.number) || ''}
    </TextHighlight>
  </React.Fragment>,
  <React.Fragment key="your-product-number">
    <NoWrap>
      {t('label.yourproductnumber')}
      :&nbsp;
    </NoWrap>
    <TextHighlight search={query.split(' ')}>
      {product.customerContextProducts[0]?.number || ''}
    </TextHighlight>
  </React.Fragment>,
]

const getOrderLabels = (query: string, order: TransformedListOrder, t: TFn) => [
  <React.Fragment key="po-number">
    <NoWrap>
      {t('label.customerponumber')}
      :&nbsp;
    </NoWrap>
    <TextHighlight search={query.split(' ')}>
      {order.purchaseOrderNumber || ''}
    </TextHighlight>
  </React.Fragment>,
  <React.Fragment key="order-number">
    <NoWrap>
      {t('label.brenntagordernumber')}
      :&nbsp;
    </NoWrap>
    <TextHighlight search={query.split(' ')}>
      {trimLeadingZeros(order.salesOrderNumber || order.quotationNumber) || ''}
    </TextHighlight>
  </React.Fragment>,
]

const getInvoiceLabels = (
  query: string,
  invoice: TransformedInvoice,
  t: TFn,
) => [
  <React.Fragment key="order-number">
    <NoWrap>
      {t('label.brenntagordernumber')}
      :&nbsp;
    </NoWrap>
    <TextHighlight search={query.split(' ')}>
      {trimLeadingZeros(invoice.delivery?.order?.salesOrderNumber) || ''}
    </TextHighlight>
  </React.Fragment>,
  <React.Fragment key="po-number">
    <NoWrap>
      {t('label.purchaseOrderNumber')}
      :&nbsp;
    </NoWrap>
    <TextHighlight search={query.split(' ')}>
      {invoice.purchaseOrderNumber || ''}
    </TextHighlight>
  </React.Fragment>,
  <React.Fragment key="delivery-number">
    <NoWrap>
      {t('label.deliverynumber')}
      :&nbsp;
    </NoWrap>
    <TextHighlight search={query.split(' ')}>
      {trimLeadingZeros(invoice.delivery?.number) || ''}
    </TextHighlight>
  </React.Fragment>,
]

// TODO: determine what data we want to show here, probably at least customer name and number?
const getUserLabels = (query: string, user: TransformedUser, t: TFn) => [
  <React.Fragment key="user-number">
    <NoWrap>
      {t('label.email')}
      :&nbsp;
    </NoWrap>
    <TextHighlight search={query.split(' ')}>{user.email || ''}</TextHighlight>
  </React.Fragment>,
]

interface SearchResultsProps {
  data?: UseSearchData
  onClick: VoidFunction
  options: UseSearchResultOptions
}

export const SearchResults = ({
  data,
  onClick,
  options: {
    invoices: showInvoices,
    orders: showOrders,
    products: showProducts,
    quotes: showQuotes,
    search = '',
    users: showUsers,
  },
}: SearchResultsProps) => {
  const t = useTranslations()
  const [tabIndex, setTabIndex] = useState(0)
  const { onSearchQueryChange } = useSearchQuery()

  const onItemClick = () => {
    onSearchQueryChange('')
    onClick()
  }

  useEffect(() => {
    if (data) {
      const { invoices, orders, products, quotes, users } = data
      const tabEnabled = ([] as boolean[])
        .concat(showOrders ? orders.total > 0 : [])
        .concat(showQuotes ? quotes.total > 0 : [])
        .concat(showProducts ? products.total > 0 : [])
        .concat(showInvoices ? invoices.total > 0 : [])
        .concat(showUsers ? users.total > 0 : [])

      const firstEnabledTab = tabEnabled.indexOf(true)
      if (firstEnabledTab !== -1) {
        setTabIndex(firstEnabledTab)
      }
    }
  }, [data, showInvoices, showProducts, showOrders, showQuotes, showUsers])

  if (!data) {
    return null
  }

  const { invoices, orders, products, quotes, users } = data

  if (
    products.total +
      orders.total +
      invoices.total +
      quotes.total +
      users.total ===
    0
  ) {
    return <Center>{t('message.noitemstoshow')}</Center>
  }

  return (
    <Tabs index={tabIndex} onChange={setTabIndex} size="sm" variant="gradient">
      <TabList flexWrap="wrap" px={4}>
        {showOrders && (
          <TabWithCount
            count={orders.total}
            data-testid="orders-search-result-tab"
          >
            {t('page.OrderList')}
          </TabWithCount>
        )}
        {showQuotes && (
          <TabWithCount
            count={quotes.total}
            data-testid="quotes-search-result-tab"
          >
            {t('page.QuoteList')}
          </TabWithCount>
        )}
        {showProducts && (
          <TabWithCount
            count={products.total}
            data-testid="products-search-result-tab"
          >
            {t('page.ProductList')}
          </TabWithCount>
        )}
        {showInvoices && (
          <TabWithCount
            count={invoices.total}
            data-testid="invoices-search-result-tab"
          >
            {t('page.InvoiceList')}
          </TabWithCount>
        )}
        {showUsers && (
          <TabWithCount
            count={users.total}
            data-testid="users-search-result-tab"
          >
            {t('page.UserList')}
          </TabWithCount>
        )}
      </TabList>
      <TabPanels>
        {showOrders && (
          <TabPanel maxHeight="75vh" overflowY="auto" px={0}>
            {orders.orders.map(order => (
              <RouterLink
                key={order.id}
                onClick={onItemClick}
                to={{
                  payload: { id: order.id },
                  type: Page.OrderDetails,
                }}
              >
                <ResultItem
                  subtitle={
                    <TextHighlight search={search.split(' ')}>
                      {order.orderLines
                        .map(line => line.product.name)
                        .join(', ')}
                    </TextHighlight>
                  }
                  title={getOrderLabels(search, order, t)}
                />
              </RouterLink>
            ))}
            {orders.orders.length < orders.total && (
              <ViewMoreLink onClick={onClick} to={{ type: Page.Orders }} />
            )}
          </TabPanel>
        )}

        {showQuotes && (
          <TabPanel maxHeight="75vh" overflowY="auto" px={0}>
            {quotes.quotes.map(quote => (
              <RouterLink key={quote.id} onClick={onItemClick} to={Page.Quotes}>
                <ResultItem
                  title={
                    <>
                      {t('label.quotenumber')}:{' '}
                      <TextHighlight search={search.split(' ')}>
                        {trimLeadingZeros(quote.number) || ''}
                      </TextHighlight>
                    </>
                  }
                />
              </RouterLink>
            ))}
            {quotes.quotes.length < quotes.total && (
              <ViewMoreLink onClick={onClick} to={{ type: Page.Quotes }} />
            )}
          </TabPanel>
        )}

        {showProducts && (
          <TabPanel maxHeight="75vh" overflowY="auto" px={0}>
            {products.products.map(product => (
              <RouterLink
                key={product.id}
                onClick={onItemClick}
                to={{
                  payload: { id: product.id },
                  type: Page.ProductDetails,
                }}
              >
                <ResultItem
                  subtitle={getProductLabels(search, product, t)}
                  title={
                    <TextHighlight search={search.split(' ')}>
                      {product.name}
                    </TextHighlight>
                  }
                />
              </RouterLink>
            ))}
            {products.products.length < products.total && (
              <ViewMoreLink onClick={onClick} to={{ type: Page.Products }} />
            )}
          </TabPanel>
        )}

        {showInvoices && (
          <TabPanel maxHeight="75vh" overflowY="auto" px={0}>
            {invoices.invoices.map(invoice => (
              <RouterLink
                key={invoice.id}
                onClick={onItemClick}
                to={{
                  payload: { id: invoice.id },
                  type: Page.InvoiceDetails,
                }}
              >
                <ResultItem
                  subtitle={getInvoiceLabels(search, invoice, t)}
                  title={
                    <>
                      {t(getInvoiceLabelByType(invoice.type))}:{' '}
                      <TextHighlight search={search.split(' ')}>
                        {trimLeadingZeros(invoice.localNumber) || ''}
                      </TextHighlight>
                    </>
                  }
                />
              </RouterLink>
            ))}
            {invoices.invoices.length < invoices.total && (
              <ViewMoreLink onClick={onClick} to={{ type: Page.Invoices }} />
            )}
          </TabPanel>
        )}

        {showUsers && (
          <TabPanel maxHeight="75vh" overflowY="auto" px={0}>
            {users.users.map(user => (
              <RouterLink
                key={user.id}
                onClick={() => onSearchQueryChange('')}
                to={{
                  payload: { userId: user.id },
                  type: Page.UserEdit,
                }}
              >
                <ResultItem
                  subtitle={getUserLabels(search, user, t)}
                  title={
                    <TextHighlight search={search.split(' ')}>
                      {user.name || ''}
                    </TextHighlight>
                  }
                />
              </RouterLink>
            ))}
            {users.users.length < users.total && (
              <ViewMoreLink to={{ type: Page.Users }} />
            )}
          </TabPanel>
        )}
      </TabPanels>
    </Tabs>
  )
}
